import { useState, useEffect } from 'react';
import { isNonSGTBrowser } from '../utils/dateUtils';
import '../assets/TimezoneNotice.css';

// Set by LoginPage on a successful login. TimezoneNotice watches for this flag
// so the popup appears AFTER login (not on every page load/refresh).
const LOGIN_FLAG = 'tz_login_check';
// Set once the notice is shown/dismissed for the current session.
const DISMISSED_KEY = 'tz_notice_dismissed';

const TimezoneNotice = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // App.js never unmounts on client-side navigation, so LoginPage sets a
    // sessionStorage flag right before navigating to the dashboard. We poll
    // briefly for that flag and only then decide whether to show the notice.
    const maybeShow = () => {
      const justLoggedIn = sessionStorage.getItem(LOGIN_FLAG) === '1';
      if (!justLoggedIn) return;

      // Consume the login flag so it only triggers once per login.
      sessionStorage.removeItem(LOGIN_FLAG);

      if (isNonSGTBrowser() && !sessionStorage.getItem(DISMISSED_KEY)) {
        setVisible(true);
      }
    };

    // Check immediately (covers the case where the flag is already set)
    maybeShow();

    // And keep watching for the flag being set after login within this session
    const interval = setInterval(maybeShow, 500);
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="tz-overlay" role="dialog" aria-modal="true" aria-labelledby="tz-title">
      <div className="tz-modal">
        <div className="tz-icon" aria-hidden="true">🕐</div>

        <h2 id="tz-title" className="tz-title">Heads up — all times are in SGT</h2>

        <p className="tz-body">
          Your device is set to{' '}
          <strong className="tz-highlight">{browserTz}</strong>, but this portal
          operates entirely in{' '}
          <strong className="tz-highlight">Singapore Time (SGT, UTC+8)</strong>.
        </p>

        <p className="tz-body">
          Every date and time shown on this portal — lessons, invoices,
          notifications — is displayed in SGT. When submitting attendance,
          enter the time the lesson took place <em>in Singapore</em>.
        </p>

        <button className="tz-dismiss-btn" onClick={handleDismiss} autoFocus>
          Got it
        </button>

        <p className="tz-footnote">This message won't appear again this session.</p>
      </div>
    </div>
  );
};

export default TimezoneNotice;
